/**
 * @file: index.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * 销售对练: where a trainee picks or describes what to practise. Setting it
 * up happens with the coach in a 知车 AI conversation; this stack is the
 * start page, the case library and the trainee's practice.
 */

import { AppNavigationProvider } from '@euler/app/components/AppNavigationProvider';
import { useStatusBarStyle } from '@euler/app/components/AppStatusBar';
import { BackIcon } from '@euler/app/components/icons/BackIcon';
import { CoachingCaseScreen } from '@euler/app/flows/coaching/pages/case';
import { CoachingCatalogScreen } from '@euler/app/flows/coaching/pages/catalog';
import { CoachingCollectionScreen } from '@euler/app/flows/coaching/pages/collection';
import { CoachingCollectionsScreen } from '@euler/app/flows/coaching/pages/collections';
import { CoachingHistoryScreen } from '@euler/app/flows/coaching/pages/history';
import { CoachingHomeScreen } from '@euler/app/flows/coaching/pages/home';
import { CoachingLeaderboardScreen } from '@euler/app/flows/coaching/pages/leaderboard';
import { CoachingPracticeScreen } from '@euler/app/flows/coaching/pages/practice';
import { CoachingRelatedCasesScreen } from '@euler/app/flows/coaching/pages/related';
import { CoachingReportScreen } from '@euler/app/flows/coaching/pages/report';
import { CoachingTranscriptScreen } from '@euler/app/flows/coaching/pages/transcript';
import { MultilineHeaderTitle } from '@euler/components/MultilineHeaderTitle';
import { ErrorFallback } from '@euler/components/error';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsDesktopLayout } from '@euler/functions/responsiveness';
import { ScreenToParams, spreadScreens } from '@euler/functions/spreadScreens';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import {
  TransitionPresets,
  createStackNavigator,
} from '@react-navigation/stack';
import * as Sentry from '@sentry/react';
import { memo } from 'react';
import { aiColors } from '../ai-suite/theme';

const Stack = createStackNavigator();

const Screens = {
  _main: CoachingHomeScreen,
  catalog: CoachingCatalogScreen,
  case: CoachingCaseScreen,
  related: CoachingRelatedCasesScreen,
  collections: CoachingCollectionsScreen,
  collection: CoachingCollectionScreen,
  history: CoachingHistoryScreen,
  practice: CoachingPracticeScreen,
  report: CoachingReportScreen,
  transcript: CoachingTranscriptScreen,
  leaderboard: CoachingLeaderboardScreen,
};

export type CoachingNavParams = ScreenToParams<typeof Screens>;

export const CoachingFlow = wrapNavigatorScreen(
  () => {
    useStatusBarStyle('dark');
    return <Content />;
  },
  { title: '销售对练', headerShown: false },
);

const Content = memo(() => {
  const isDesktop = useIsDesktopLayout();
  const { rpx } = useResponsiveContext();
  return (
    <AppNavigationProvider>
      <Sentry.ErrorBoundary
        // @ts-ignore
        fallback={(params: any) => <ErrorFallback {...params} />}
      >
        <Stack.Navigator
          initialRouteName="_main"
          screenOptions={{
            ...TransitionPresets.SlideFromRightIOS,
            headerMode: 'float',
            headerTitleAlign: isDesktop ? 'left' : 'center',
            headerBackTitle: ' ',
            headerTitleStyle: {
              fontSize: rpx(isDesktop ? 20 : 17),
              fontFamily: FontFamily.NotoSans.Bold,
              color: aiColors.foreground,
            },
            headerStyle: {
              backgroundColor: aiColors.card,
              borderBottomWidth: 1,
              borderBottomColor: aiColors.border,
              shadowOpacity: 0,
              elevation: 0,
            },
            cardStyle: { backgroundColor: aiColors.background },
            headerTitle: MultilineHeaderTitle,
            headerBackImage: () => <BackIcon fill={aiColors.foreground} />,
          }}
        >
          {spreadScreens(Stack.Screen, Screens, '_main')}
        </Stack.Navigator>
      </Sentry.ErrorBoundary>
    </AppNavigationProvider>
  );
});
